// The ad form (M14): the template's fields in Spanish and English, the price, the photo, the brand, and
// Save PNG / PDF. Only the fields the template shows are listed. Typing in one field is one undo step.
import { useState, type ReactNode } from 'react';
import { useT, type MessageKey } from '../../../i18n';
import { useDesign } from '../../state';
import { setAd, imageNodes, textNodes } from '../../templates/instantiate';
import { formatPrice, parsePrice, usedRoles } from '../../ad/fields';
import { exportScales } from '../../io/exportAd';
import { fillPhotoSlot, fillSlotFromFile, saveAd, useAdUi } from '../../ad/actions';
import { updateNode } from '../../commands/commands';
import { useLibrary } from '../../../state/library';
import type { AdData, Bi } from '../../model/types';
import s from './Ad.module.css';
import { useAi } from '../../../features/ai/aiSettings';
import { adTextForAd, useAiUi } from '../../../features/ai/aiActions';

type BiKey = 'dishName' | 'description' | 'headline' | 'tagline';
const BI_FIELDS: { key: BiKey; label: MessageKey; long?: boolean }[] = [
  { key: 'dishName', label: 'adDishName' },
  { key: 'description', label: 'adDescription', long: true },
  { key: 'headline', label: 'adHeadline' },
  { key: 'tagline', label: 'adTagline' },
];

const end = () => useDesign.getState().endGesture();

function PriceInput({
  value,
  onChange,
  testId,
  label,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  testId: string;
  label: string;
}) {
  const [text, setText] = useState<string | null>(null);
  return (
    <label className={s.field}>
      <span>{label}</span>
      <input
        type="text"
        inputMode="decimal"
        data-testid={testId}
        value={text ?? (value === null ? '' : String(value))}
        onChange={(e) => {
          setText(e.target.value);
          onChange(parsePrice(e.target.value));
        }}
        onBlur={() => {
          setText(null);
          end();
        }}
      />
    </label>
  );
}

function Section({ title, children }: { title: MessageKey; children: ReactNode }) {
  const t = useT();
  return (
    <>
      <h4>{t(title)}</h4>
      {children}
    </>
  );
}

export function AdForm() {
  const t = useT();
  const doc = useDesign((d) => d.doc);
  const busy = useAdUi((a) => a.busy);
  const photos = useLibrary((l) => l.photos);
  const active = useLibrary((l) => l.activeId);
  const [msg, setMsg] = useState<string | null>(null);
  const [scaleId, setScaleId] = useState<string | null>(null);
  const aiOn = useAi((a) => !!a.settings?.key);
  const aiBusy = useAiUi((a) => a.busy);
  const aiError = useAiUi((a) => a.error);
  if (!doc) return null;
  if (!doc.ad) return <p className={s.small}>{t('adNotAnAd')}</p>;
  const ad = doc.ad;
  const used = usedRoles(textNodes(doc));
  const run = (fn: (a: AdData) => void, merge: string) => useDesign.getState().run(setAd(fn, merge));
  const slot = imageNodes(doc, 'photo')[0];
  const scales = exportScales(doc);
  const scale = scales.find((x) => x.id === scaleId) ?? scales[scales.length - 1]!;
  const currency = doc.brand?.currency;

  const save = async (kind: 'png' | 'pdf') => {
    useAdUi.setState({ busy: kind });
    setMsg(null);
    try {
      setMsg(t('saved', { name: await saveAd(kind, scale) }));
    } catch (e) {
      console.warn('ad save failed', e);
      setMsg(t('saveFailed', { reason: (e as Error).message }));
    } finally {
      useAdUi.setState({ busy: null });
    }
  };

  const bi = (key: BiKey, label: MessageKey, long?: boolean) => {
    const v = ad[key] as Bi;
    const input = (lang: 'es' | 'en') =>
      long ? (
        <textarea
          rows={2}
          data-testid={`ad-${key}-${lang}`}
          value={v[lang]}
          onChange={(e) => run((a) => void ((a[key] as Bi)[lang] = e.target.value), `ad-${key}-${lang}`)}
          onBlur={end}
        />
      ) : (
        <input
          type="text"
          data-testid={`ad-${key}-${lang}`}
          value={v[lang]}
          onChange={(e) => run((a) => void ((a[key] as Bi)[lang] = e.target.value), `ad-${key}-${lang}`)}
          onBlur={end}
        />
      );
    return (
      <div key={key}>
        <label className={s.field}>
          <span>
            {t(label)} — {t('adSpanish')}
          </span>
          {input('es')}
        </label>
        <label className={s.field}>
          <span>
            {t(label)} — {t('adEnglish')}
          </span>
          {input('en')}
        </label>
      </div>
    );
  };

  return (
    <div data-testid="ad-form">
      {aiOn && (
        <>
          <button
            className={s.big}
            data-testid="ai-ad-text"
            disabled={!!aiBusy}
            onClick={() => void adTextForAd()}
          >
            ✨ {t('aiWriteText')}
            {aiBusy === 'adText' ? ' …' : ''}
          </button>
          {aiError && (
            <div className={s.small} style={{ color: '#ff6b6b' }}>
              {t('aiFailed', { reason: aiError })}
            </div>
          )}
        </>
      )}
      <Section title="adText">
        {BI_FIELDS.filter((f) => used.roles.has(f.key)).map((f) => bi(f.key, f.label, f.long))}
        <div className={s.pair}>
          {used.roles.has('price') && (
            <PriceInput
              label={t('adPrice')}
              testId="ad-price"
              value={ad.price}
              onChange={(v) => run((a) => void (a.price = v), 'ad-price')}
            />
          )}
          {used.roles.has('oldPrice') && (
            <PriceInput
              label={t('adOldPrice')}
              testId="ad-old-price"
              value={ad.oldPrice}
              onChange={(v) => run((a) => void (a.oldPrice = v), 'ad-old-price')}
            />
          )}
        </div>
        {used.roles.has('price') && (
          <div className={s.small}>{t('adPriceShows', { p: formatPrice(ad.price, currency) || '—' })}</div>
        )}
      </Section>

      {used.items > 0 && (
        <Section title="adMenuItems">
          {Array.from({ length: used.items }, (_, i) => {
            const it = ad.items[i] ?? { name: { es: '', en: '' }, price: null };
            const upd = (fn: (x: AdData['items'][number]) => void, k: string) =>
              run((a) => {
                while (a.items.length <= i) a.items.push({ name: { es: '', en: '' }, price: null });
                fn(a.items[i]!);
              }, `ad-item-${i}-${k}`);
            return (
              <div
                key={i}
                style={{ borderBottom: '1px solid var(--line)', paddingBottom: 4, marginBottom: 6 }}
              >
                <div className={s.pair}>
                  <label className={s.field}>
                    <span>
                      {i + 1}. {t('adSpanish')}
                    </span>
                    <input
                      type="text"
                      data-testid={`ad-item-${i}-es`}
                      value={it.name.es}
                      onChange={(e) => upd((x) => void (x.name.es = e.target.value), 'es')}
                      onBlur={end}
                    />
                  </label>
                  <label className={s.field}>
                    <span>{t('adEnglish')}</span>
                    <input
                      type="text"
                      data-testid={`ad-item-${i}-en`}
                      value={it.name.en}
                      onChange={(e) => upd((x) => void (x.name.en = e.target.value), 'en')}
                      onBlur={end}
                    />
                  </label>
                </div>
                <PriceInput
                  label={t('adPrice')}
                  testId={`ad-item-${i}-price`}
                  value={it.price}
                  onChange={(v) => upd((x) => void (x.price = v), 'price')}
                />
              </div>
            );
          })}
        </Section>
      )}

      {slot && (
        <Section title="adPhoto">
          {active && (
            <button
              className={s.big}
              data-testid="ad-use-current-photo"
              disabled={!!busy}
              onClick={() => void fillPhotoSlot(slot.id, active)}
            >
              📷 {t('adUseCurrentPhoto')}
            </button>
          )}
          {photos.length > 1 && (
            <label className={s.field}>
              <span>{t('adChoosePhoto')}</span>
              <select
                data-testid="ad-choose-photo"
                value=""
                onChange={(e) => e.target.value && void fillPhotoSlot(slot.id, e.target.value)}
              >
                <option value="">—</option>
                {photos.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button
            style={{ marginTop: 4 }}
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'image/png,image/jpeg,image/webp';
              input.onchange = () => input.files?.[0] && void fillSlotFromFile(slot.id, input.files[0]);
              input.click();
            }}
          >
            {t('adPhotoFromFile')}
          </button>
          {slot.assetId && (
            <>
              <label className={s.field} style={{ marginTop: 8 }}>
                <span>{t('adPhotoZoom')}</span>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.05}
                  data-testid="ad-photo-zoom"
                  value={slot.zoom}
                  onChange={(e) =>
                    useDesign
                      .getState()
                      .run(
                        updateNode(
                          slot.id,
                          (n) => n.kind === 'image' && (n.zoom = Number(e.target.value)),
                          'cmdEdit',
                          'ad-zoom',
                        ),
                      )
                  }
                  onPointerUp={end}
                />
              </label>
              <div className={s.pair}>
                <label className={s.field}>
                  <span>{t('adPhotoLeftRight')}</span>
                  <input
                    type="range"
                    min={-0.5}
                    max={0.5}
                    step={0.01}
                    value={slot.offsetX}
                    onChange={(e) =>
                      useDesign
                        .getState()
                        .run(
                          updateNode(
                            slot.id,
                            (n) => n.kind === 'image' && (n.offsetX = Number(e.target.value)),
                            'cmdEdit',
                            'ad-ox',
                          ),
                        )
                    }
                    onPointerUp={end}
                  />
                </label>
                <label className={s.field}>
                  <span>{t('adPhotoUpDown')}</span>
                  <input
                    type="range"
                    min={-0.5}
                    max={0.5}
                    step={0.01}
                    value={slot.offsetY}
                    onChange={(e) =>
                      useDesign
                        .getState()
                        .run(
                          updateNode(
                            slot.id,
                            (n) => n.kind === 'image' && (n.offsetY = Number(e.target.value)),
                            'cmdEdit',
                            'ad-oy',
                          ),
                        )
                    }
                    onPointerUp={end}
                  />
                </label>
              </div>
              <label>
                <input
                  type="checkbox"
                  checked={slot.fit === 'contain'}
                  onChange={(e) => {
                    useDesign
                      .getState()
                      .run(
                        updateNode(
                          slot.id,
                          (n) => n.kind === 'image' && (n.fit = e.target.checked ? 'contain' : 'cover'),
                        ),
                      );
                    end();
                  }}
                />{' '}
                {t('adPhotoWhole')}
              </label>
            </>
          )}
        </Section>
      )}

      <Section title="adSave">
        <label className={s.field}>
          <span>{t('adPngSize')}</span>
          <select data-testid="ad-png-size" value={scale.id} onChange={(e) => setScaleId(e.target.value)}>
            {scales.map((x) => (
              <option key={x.id} value={x.id}>
                {x.label}
              </option>
            ))}
          </select>
        </label>
        <button
          className={`${s.big} ${s.primary}`}
          data-testid="ad-save-png"
          disabled={!!busy}
          onClick={() => void save('png')}
        >
          💾 {t('adSavePng')}
          {busy === 'png' ? ' …' : ''}
        </button>
        <button
          className={s.big}
          data-testid="ad-save-pdf"
          disabled={!!busy}
          onClick={() => void save('pdf')}
        >
          📄 {t('adSavePdf')}
          {busy === 'pdf' ? ' …' : ''}
        </button>
        {msg && (
          <div role="status" className={s.status} data-testid="ad-save-status">
            {msg}
          </div>
        )}
      </Section>
    </div>
  );
}
